// HelpMenu — nút «Trợ giúp» trên thanh trên cùng (giao diện v2). DÙNG CHUNG CNV Work + Site Connect
// (sếp Phú 13/10/2026; gốc là HelpMenuV2 của Site Connect 11/10/2026). CSS: @cnv/ui/styles/ui-v2-help.css
// (host nạp tĩnh ở main.jsx — Core không có cụm này nên không nằm trong ui-v2.css).
//
// Menu: «Chat với hỗ trợ» (site hỗ trợ là loại chat) · gửi góp ý / báo lỗi · danh sách yêu cầu (của cả
// workspace khi máy chủ đã ký claim workspace) · trung tâm trợ giúp (tab mới). Dưới cùng là THÔNG BÁO:
// yêu cầu vừa có cập nhật từ đội hỗ trợ (đổi trạng thái / trả lời) — Site Connect không có chuông «Tin mới»
// nên đây là chỗ khách thấy (sếp Phú: «cần làm thêm tính năng thông báo giống worksaas»). Số đỏ trên nút =
// yêu cầu có cập nhật chưa xem. Bấm một dòng ⇒ mở khung hỗ trợ ngay tại yêu cầu đó.
//
// Nút CHỈ hiện khi widget đã dựng xong (cụm đã cấu hình + máy chủ hỗ trợ trả lời): cụm chưa cấu hình /
// mạng chặn thì không hiện một nút bấm ra lỗi.

import { useEffect, useRef, useState } from 'react';
import { HelpCircle, MessageSquarePlus, MessagesSquare, Inbox, BookOpen, ExternalLink } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { getHelpState, subscribeHelp, openHelpNew, openHelpList, openHelpChat, closeHelpPanel } from './helpWidget';
import {
  useSupportTickets, refreshSupportTickets, openSupportTicket, markAllSupportTicketsSeen,
} from '../product-news/supportTicketFeed';
import { fmtDateTime } from '../../i18n/format.js';

const MAX_UPDATES = 5;

export default function HelpMenu() {
  const { t } = useLingui();
  const [state, setState] = useState(getHelpState);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const tk = useSupportTickets();

  useEffect(() => subscribeHelp(setState), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!state.ready) return null;

  const pick = (fn) => () => { setOpen(false); fn(); };
  const toggle = () => {
    if (!open) {
      refreshSupportTickets({ force: true });
      // Menu sống trong thanh trên cùng (lớp 60) — khung chat cỡ lớn (lớp 2147483000) sẽ đè lên nó.
      closeHelpPanel();
    }
    setOpen(!open);
  };
  const workspace = state.workspaceScope || tk.scope === 'workspace';
  const unread = tk.unread;
  const updates = tk.items.slice(0, MAX_UPDATES);

  return (
    <div className="v2-help" ref={boxRef}>
      <button
        type="button"
        className="v2-help__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        title={t`Trợ giúp & góp ý`}
      >
        <HelpCircle size={15} aria-hidden="true" />
        <span className="v2-help__label">{t`Trợ giúp`}</span>
        {unread > 0 && (
          <span className="v2-help__badge" aria-label={t`Có phản hồi mới`}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div className="v2-help__menu" role="menu">
          {state.chat && (
            <button type="button" role="menuitem" className="v2-help__item" onClick={pick(openHelpChat)}>
              <MessagesSquare size={16} aria-hidden="true" />
              <span>{t`Chat với hỗ trợ`}</span>
            </button>
          )}
          <button type="button" role="menuitem" className="v2-help__item" onClick={pick(openHelpNew)}>
            <MessageSquarePlus size={16} aria-hidden="true" />
            <span>{t`Gửi góp ý / Báo lỗi`}</span>
          </button>
          <button type="button" role="menuitem" className="v2-help__item" onClick={pick(openHelpList)}>
            <Inbox size={16} aria-hidden="true" />
            <span>{workspace ? t`Yêu cầu của workspace` : t`Yêu cầu của tôi`}</span>
            {unread > 0 && <b className="v2-help__count">{unread}</b>}
          </button>
          {state.helpUrl ? (
            <a
              role="menuitem"
              className="v2-help__item"
              href={state.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              <BookOpen size={16} aria-hidden="true" />
              <span>{t`Trung tâm trợ giúp`}</span>
              <ExternalLink size={13} className="v2-help__ext" aria-hidden="true" />
            </a>
          ) : null}

          {updates.length > 0 && (
            <div className="v2-help__updates">
              <div className="v2-help__updates-head">
                <span>{t`Cập nhật yêu cầu`}</span>
                {unread > 0 && (
                  <button type="button" className="v2-help__mark" onClick={() => markAllSupportTicketsSeen()}>
                    {t`Đánh dấu đã đọc`}
                  </button>
                )}
              </div>
              {updates.map((u) => {
                // Chữ trung tính — menu này chạy cả ở Site Connect (không lộ thương hiệu CNV).
                const who = u.by || t`Đội hỗ trợ`;
                return (
                <button
                  key={u.id}
                  type="button"
                  role="menuitem"
                  className={`v2-help__update${u.has_news ? ' is-new' : ''}`}
                  onClick={pick(() => openSupportTicket(u.id))}
                >
                  <span className="v2-help__update-title">
                    {u.code ? `${u.code} · ` : ''}{u.title}
                  </span>
                  <span className="v2-help__update-meta">
                    {u.kind === 'reply' && u.excerpt ? (
                      <span className="v2-help__update-text">{t`${who} trả lời: ${u.excerpt}`}</span>
                    ) : (
                      <span className={`v2-help__pill v2-help__pill--${u.status_tone}`}>{u.status_label}</span>
                    )}
                    <span className="v2-help__update-time">{fmtDateTime(u.at)}</span>
                  </span>
                  {!u.mine && u.requester_name ? (
                    <span className="v2-help__update-who">{t`Gửi bởi ${u.requester_name}`}</span>
                  ) : null}
                  {u.has_news && <span className="v2-help__update-dot" aria-label={t`Chưa đọc`} />}
                </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
